import {
	MasterInfoEnum,
	ROUTERS,
	TextMessage,
	UpdateFieldObjectNameEnum,
} from '@/constant';
import {
	useCommonList,
	useRowSelection,
	useSearchFilterRef,
	useToggle,
} from '@/hooks';
import { useMergingState } from '@/hooks';
import { ICommonAssignmentApplicant } from '@/services';
import { CommonActions, useAppDispatch } from '@/stores';
import { dayjs, showAlert, SUPPORT_FORMAT_DATE_LIST } from '@/utils';
import { useCallback, useEffect, useState } from 'react';

import { getColumns, TYPE_SEARCH_FRANCHISE_OPTIONS } from './config';
import { SelectAssignmentModalViewProps, TFilter } from './type';

interface SearchFranchise {
	searchValue: string;
	searchType: string;
	excludeFranchiseId?: number;
	franchiseId?: number;
	page: number;
}

export const useLogic = (props: SelectAssignmentModalViewProps) => {
	const {
		allowSelectMultiple = false,
		excludeFranchiseId,
		franchiseId,
		openFrom,
		franchiseName,
	} = props;

	const rowSelection = useRowSelection<any, ICommonAssignmentApplicant>({
		hideSelectAll: !allowSelectMultiple,
		isSingle: !allowSelectMultiple,
		getCheckboxProps: (record: ICommonAssignmentApplicant) => {
			const disabled =
				openFrom === ROUTERS.WC1_02 &&
				!!record.assignmentFranchiseEndDate &&
				dayjs(record.assignmentFranchiseEndDate, SUPPORT_FORMAT_DATE_LIST)
					.startOf('day')
					.isBefore(dayjs().startOf('day'));
			return {
				disabled,
			};
		},
	});

	const defaultFilter = {
		valueSelect:
			openFrom === ROUTERS.BM1_03 || openFrom === ROUTERS.BM1_01
				? TYPE_SEARCH_FRANCHISE_OPTIONS[2].value
				: TYPE_SEARCH_FRANCHISE_OPTIONS[0].value,
		keyword: '',
	};
	const [filter, setFilter] = useMergingState<TFilter>(defaultFilter);

	const resetFilter = useCallback(() => {
		setFilter(defaultFilter);
		setParamSearch({
			searchValue: '',
			searchType: defaultFilter.valueSelect,
			page: 1,
		});
		rowSelection.onReset();
	}, []);

	const [openModal, onToggle] = useToggle(false, resetFilter);

	const [paramSearch, setParamSearch] = useState<SearchFranchise>({
		searchValue: '',
		searchType: '',
		page: 1,
	});

	const [isSearching, setIsSearching] = useState<boolean>(false);

	const {
		commonList: { fetching, data, total },
		fetchData,
	} = useCommonList<ICommonAssignmentApplicant>(
		MasterInfoEnum.ASSIGNMENT_APPLICANT,
		openModal,
		{ ...paramSearch, excludeFranchiseId, franchiseId }
	);

	const dispatch = useAppDispatch();

	const searchFilterRef = useSearchFilterRef({
		onSearchChange: fetchData,
	});

	useEffect(() => {
		if (searchFilterRef.current) {
			searchFilterRef.current.onSearchChange = fetchData;
		}
	}, [fetchData]);

	const handleSave = useCallback(() => {
		if (rowSelection?.selectedRowKeys?.length === 0) {
			showAlert(TextMessage.NO_SELECT_ASSIGNMENT);
			return;
		}

		if (props.allowSelectMultiple) {
			const assignments = data.filter((f: ICommonAssignmentApplicant) =>
				rowSelection.selectedRowKeys.includes(f?.assignmentApplicantId)
			);

			if (openFrom === ROUTERS.EM1_02) {
				const uniqueFranchiseIds = new Set(
					assignments.map(
						(a) => `${a?.franchiseId}${a?.assignmentFranchiseProgramId}`
					)
				);
				if (uniqueFranchiseIds.size > 1) {
					showAlert(TextMessage.ALERT_53);
					return;
				}
			}

			rowSelection.onReset();
			props.onSelectAssignments?.(assignments);
			onToggle();

			return;
		}

		const assignment = data.find((f: ICommonAssignmentApplicant) =>
			rowSelection.selectedRowKeys.includes(f?.assignmentApplicantId)
		);

		if (assignment) {
			if (openFrom === ROUTERS.BM1_01) {
				dispatch(
					CommonActions.updateFieldsRequest({
						data: {
							objectName: UpdateFieldObjectNameEnum.UNAUTHORIZED_DEPARTURE,
							id: props?.unauthorizedDepartureId || '',
							data: { applicantId: assignment?.applicantId },
						},
						callback: props?.fetchData,
					})
				);
			}
			rowSelection.onReset();
			props.onSelectAssignment?.(assignment);
			onToggle();
		}
	}, [onToggle, rowSelection, allowSelectMultiple]);

	const handleChangeSelect = useCallback(
		(value: string) => {
			setFilter({ valueSelect: value });
		},
		[setFilter]
	);

	const handleChangeKeyword = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setFilter({ keyword: e.target.value });
		},
		[setFilter]
	);

	const handleSelectMasterInfo = useCallback(
		(value: string) => {
			setFilter({ keyword: value });
		},
		[setFilter]
	);

	const onSearch = useCallback(() => {
		setParamSearch({
			searchValue: filter?.keyword,
			searchType: filter?.valueSelect,
			page: 1,
		});
		setIsSearching(true);
	}, [setFilter]);

	const onPageChange = useCallback((page: number) => {
		setParamSearch((prev) => ({ ...prev, page }));
	}, []);

	return {
		openModal,
		dataSource: data,
		columns: getColumns(),
		rowSelection,
		searchFilterRef,
		onToggle,
		handleSave,
		loading: fetching,
		filter,
		handleChangeSelect,
		handleChangeKeyword,
		handleSelectMasterInfo,
		onSearch,
		franchiseName,
		openFrom,
		isSearching,
		total,
		onPageChange,
	};
};
